import { GlassCard } from '@/components/design-system';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Address, Time } from '@/lib/db-client';
import { setListings } from '@/lib/collections/listings';
import { cn, generateRandomId } from '@/lib/utils';
import {
  CITIES,
  computeEstimatedValueCents,
  formatCentsAsUsd,
  PLASTIC_TYPE_LABELS,
  PLASTIC_TYPES,
  type City,
  type PlasticType,
  type SmartScanResult,
} from '@/utils/plastic-rates';
import { MapPin, PackageCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

interface CreateListingFormProps {
  scan: SmartScanResult;
  photoUrl?: string;
  previewUrl: string;
  userAddress: string;
  defaultCity?: string;
  onCreated: () => void;
}

const listingSchema = z.object({
  plasticType: z.enum(PLASTIC_TYPES),
  estimatedKg: z.number().positive('Enter a weight greater than 0').max(500, 'That seems too heavy — double check'),
  city: z.enum(CITIES, { message: 'Choose a city' }),
  locationName: z.string().trim().min(2, 'Add a short location').max(80, 'Location is too long'),
});

/** Pre-filled from the Smart Scan result; household confirms/adjusts before publishing. */
export function CreateListingForm({
  scan,
  photoUrl,
  previewUrl,
  userAddress,
  defaultCity,
  onCreated,
}: CreateListingFormProps) {
  const [plasticType, setPlasticType] = useState<PlasticType>(scan.plasticType);
  const [estimatedKg, setEstimatedKg] = useState(scan.estimatedKg);
  const [city, setCity] = useState<'' | City>(
    (CITIES as readonly string[]).includes(defaultCity ?? '') ? (defaultCity as City) : ''
  );
  const [locationName, setLocationName] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [geoStatus, setGeoStatus] = useState<'idle' | 'locating' | 'success' | 'denied'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoStatus('denied');
      return;
    }
    setGeoStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
        setGeoStatus('success');
      },
      () => setGeoStatus('denied'),
      { timeout: 8000, maximumAge: 60_000 }
    );
  }, []);

  const estimatedValue = computeEstimatedValueCents(plasticType, estimatedKg);

  const handleSubmit = async () => {
    const result = listingSchema.safeParse({ plasticType, estimatedKg, city, locationName });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        fieldErrors[String(issue.path[0])] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const listingId = generateRandomId();
      const success = await setListings(listingId, {
        createdBy: Address.publicKey(userAddress),
        plasticType: result.data.plasticType,
        estimatedKg: result.data.estimatedKg,
        estimatedValue,
        ...(photoUrl ? { photoUrl } : {}),
        city: result.data.city,
        locationName: result.data.locationName,
        lat,
        lng,
        status: 'open',
        createdAt: Time.Now,
      });

      if (success) {
        toast.success('Listing published — the network can see it now.');
        onCreated();
      } else {
        toast.error('Could not publish your listing. Please try again.');
      }
    } catch (err) {
      console.error('Create listing failed', err);
      toast.error('Something went wrong publishing your listing.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GlassCard padding="lg" className="space-y-5">
      <div className="flex items-center gap-3">
        <img src={previewUrl} alt="" className="h-14 w-14 rounded-xl object-cover" />
        <div>
          <span className="text-overline">Confirm & publish</span>
          <h2 className="font-heading text-xl font-bold text-foreground">Create your listing</h2>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Plastic type</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {PLASTIC_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setPlasticType(type)}
              className={cn(
                'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all',
                plasticType === type
                  ? 'border-primary/60 bg-gradient-to-r from-primary/15 to-accent/15 text-foreground'
                  : 'border-border/60 bg-secondary/30 text-muted-foreground hover:border-border'
              )}
            >
              {PLASTIC_TYPE_LABELS[type]}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Weight</Label>
          <span className="font-heading text-lg font-bold text-foreground">{estimatedKg.toFixed(1)} kg</span>
        </div>
        <Slider
          value={[estimatedKg]}
          onValueChange={([v]) => setEstimatedKg(v)}
          min={0.5}
          max={100}
          step={0.5}
          className="mt-3"
        />
        {errors.estimatedKg && <p className="mt-2 text-xs text-destructive">{errors.estimatedKg}</p>}
      </div>

      <div className="rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 p-4 ring-1 ring-border/60">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">Estimated worth</span>
        <p className="font-heading text-2xl font-bold">
          <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            {formatCentsAsUsd(estimatedValue)}
          </span>
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">City</Label>
          <Select value={city} onValueChange={(v) => setCity(v as City)}>
            <SelectTrigger className="mt-2">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.city && <p className="mt-2 text-xs text-destructive">{errors.city}</p>}
        </div>

        <div>
          <Label htmlFor="locationName" className="text-xs uppercase tracking-wide text-muted-foreground">
            Location
          </Label>
          <Input
            id="locationName"
            placeholder="e.g. Ojodu, near market"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            className="mt-2"
          />
          {errors.locationName && <p className="mt-2 text-xs text-destructive">{errors.locationName}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <MapPin className="h-3.5 w-3.5" />
        {geoStatus === 'locating' && <span>Finding your location&hellip;</span>}
        {geoStatus === 'success' && <span>Location captured</span>}
        {geoStatus === 'denied' && <span>Location unavailable — add a location name above</span>}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        size="lg"
        className="w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
      >
        <PackageCheck className="mr-2 h-4 w-4" />
        {submitting ? 'Publishing...' : 'Publish listing'}
      </Button>
    </GlassCard>
  );
}

export default CreateListingForm;
